import { useState, type KeyboardEvent } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/shared/ui/buttons/Button';
import { cn } from '@/shared/utils/cn';

type Props = {
  value: string[];
  onChange: (v: string[]) => void;
  error?: string;
  label?: string;
  placeholder?: string;
  addLabel?: string;
};

export const FeaturesSupportStaffLanguagesInput = ({
  value,
  onChange,
  error,
  label,
  placeholder,
  addLabel,
}: Props) => {
  const [draft, setDraft] = useState('');

  const add = (): void => {
    const next = draft.trim();
    if (!next) return;
    if (value.includes(next)) {
      setDraft('');
      return;
    }
    onChange([...value, next]);
    setDraft('');
  };

  const remove = (lang: string): void => {
    onChange(value.filter((l) => l !== lang));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label && <span className="text-sm font-medium text-ink-soft">{label}</span>}
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'flex flex-1 items-center gap-2 rounded-lg border bg-white px-3 shadow-sm transition-colors',
            error
              ? 'border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-200'
              : 'border-slate-300 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-200',
          )}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="min-w-0 flex-1 bg-transparent py-2.5 text-sm text-ink outline-none placeholder:text-ink-faint"
            aria-invalid={Boolean(error)}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={add}
        >
          {addLabel}
        </Button>
      </div>
      {value.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {value.map((lang) => (
            <span
              key={lang}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700"
            >
              {lang}
              <button
                type="button"
                aria-label={`${lang} ✕`}
                onClick={() => remove(lang)}
                className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-800"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {error && <p className="text-xs font-medium text-red-600">{error}</p>}
    </div>
  );
};
